import * as React from 'react';

import {
    Button,
    Col,
    Row,
} from 'reactstrap';

import '../../Main.scss';

import {
    Tooltip
} from '@mui/material';

import {
    faTrash,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    AccountingPackage,
    ConnectionDescriptor,
    PortalTextCommon,
    PortalTextSubmissionHistory,
    UserCapability,
} from '../../../Models/Api/strongbox.financialportal';

import { FinancialsRelinkButton } from '../../FinancialsImport/FinancialsRelinkButton';


import { ErrorBanner, ErrorState } from '../../ErrorBanner/ErrorBanner';
import { LoadingMessageWithWaitIndicator } from '../../LoadingMessageWithIndicator/LoadingMessageWithIndicator';

import { RunningImport } from '../../../Store/RunningImports';
import { BrandConfig } from '../../../Store/Tenant';
import { DeleteConnectionFromWorkspace } from '../../../Store/Workspaces';

import { RunningImportsList } from '../../RunningImportsList/RunningImportsList';

import { resolveAccountingPackage, toAccountingPackageNameId, UniversalCollectorAccountingPackageNameId } from '../../../Models/Api/AccountingPackage';
import { FileUploadDataSourceNameId } from '../../../Utils/Constants';
import { LogMessage, SeverityLevel } from '../../../Utils/Logging';
import { ConfirmModal } from '../../ConfirmModal/ConfirmModal';

type Props = {
    connections: ConnectionDescriptor[];
    capabilities: UserCapability[];
    workspaceId: string;
    workspaceDisplayName: string;
    runningImports: RunningImport[]
    onRunningJobDismiss?: (financialRecordId: string, workspaceId: string, fromNav: boolean) => void;
    brandConfig: BrandConfig;
    commonText?: PortalTextCommon;
    submissionHistoryText?: PortalTextSubmissionHistory;
    accountingPackages: AccountingPackage[];
}

export const WorkspaceConnections: React.FC<Props> = (props): React.ReactElement => {
    const {
        capabilities,
        connections,
        runningImports,
        workspaceId,
        workspaceDisplayName,
        onRunningJobDismiss,
        commonText,
        submissionHistoryText,
        accountingPackages,
    } = props;

    const [working, setWorking] = React.useState<boolean>(false);
    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);

    const [confirmDelete, setConfirmDelete] = React.useState<{ cxnId: string, cxnDescription: string } | undefined>(undefined);

    const connectionsInUse = React.useMemo(() => {
        // The universal collector (file upload) is a one-way upload — the Manage Active Connections actions
        // (sync / disconnect) don't apply to it, so filter it out alongside the legacy file-upload source.
        return connections.filter(c => {
            if (!c.datasourceNameId) {
                // Shouldn't really happen but dataSourceNameId is defined as optional
                return false;
            }
            const key = c.datasourceNameId.toLowerCase();

            return key !== FileUploadDataSourceNameId.toLowerCase() && key !== UniversalCollectorAccountingPackageNameId.toLowerCase();
        });
    }, [connections]);

    const actionButtonStyle: React.CSSProperties = {
        padding: 8,
        marginRight: 10,
    };

    const getActions = (connection: ConnectionDescriptor, connectionIndex: number): React.ReactElement[] => {
        const actions: React.ReactElement[] = [];

        if (capabilities.some(c => c === 'CanDeleteWorkspaceConnection')) {
            actions.push((
                <Tooltip
                    key={`delete-connection-tip-${connectionIndex}`}
                    title={submissionHistoryText?.deleteConnection || ''}
                >
                    <span>
                        <Button
                            key={`delete-connection-${connectionIndex}`}
                            style={{
                                ...actionButtonStyle
                            }}
                            onClick={() => {
                                setConfirmDelete({
                                    cxnId: connection.id,
                                    cxnDescription: connection.datasetName || (commonText?.unknown || '')
                                });
                            }}
                            disabled={working}
                            color="secondary"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </span>
                </Tooltip>

            ));
        }

        const accountingPkg = toAccountingPackageNameId(connection.datasourceNameId);

        if (!!accountingPkg) {
            actions.push((
                <div key={`financials-relink-button-${connectionIndex}`}>
                    <FinancialsRelinkButton
                        connection={connection}
                        datasourceNameId={connection.datasourceNameId}
                        disabledByProp={working}
                        onSetErrorState={setErrorState}
                        onSetImporting={setWorking}
                        workspaceId={workspaceId}
                        workspaceDisplayName={workspaceDisplayName}
                    />
                </div>
            ))
        } else {
            LogMessage(
                `Cannot get accounting package from datasourceNameId in WorkspaceConnections ${connection.datasourceNameId}`,
                SeverityLevel.Warning,
                {
                    datasourceNameId: connection.datasourceNameId,
                    workspaceId,
                    connectionId: connection.id,
                }
            );
        }

        return actions;
    }
    // render
    const noConnections = (
        <p>
            No connections
        </p>
    );

    const jobIsRunning = (): boolean => {
        return !!runningImports.find(job => job.workspaceId === workspaceId);
    }

    const connectionsList = connectionsInUse && (connectionsInUse.length > 0) && (
        <div className={'workspace-list'}>
            {working && (
                <LoadingMessageWithWaitIndicator
                    msg={commonText?.working || ''}
                />
            )}
            {
                !!confirmDelete && (
                    <ConfirmModal
                        onTerminalButton={(yes?: boolean): void => {
                            if (!!yes) {
                                DeleteConnectionFromWorkspace(workspaceId, confirmDelete.cxnId)
                                    .then(success => {
                                        if (!success) {
                                            setErrorState({
                                                summaryMessage: submissionHistoryText?.errorMsgDeletingConnection || ''
                                            });
                                        }
                                    })
                                    .finally(() => {
                                        setWorking(false);
                                    });
                                setWorking(true);
                            }
                            setConfirmDelete(undefined);
                        }}
                        msg={`Please confirm that you would like to delete connection ${confirmDelete.cxnDescription}.`}
                        title={`Delete connection`}
                        commonText={commonText}
                    />
                )
            }
            {
                !!errorState && (
                    <Row className={'data-container  data-container-primary'}>
                        <Col>
                            <ErrorBanner
                                errorState={errorState}
                                onDefaultActionButton={() => {
                                    setErrorState(undefined);
                                }}
                                commonText={commonText}
                            />
                        </Col>
                    </Row>
                )
            }
            {
                jobIsRunning() && (
                    <Row className={'data-container  data-container-primary'}>
                        <Col>
                            <div>
                                <h1
                                    style={{ marginBottom: '3em' }}
                                >
                                    {commonText?.pendingDataCollections || ''}
                                </h1>
                                <RunningImportsList
                                    scopeWorkspaceId={workspaceId}
                                    hideWorkspaceInfo={true}
                                    onJobDismiss={onRunningJobDismiss}
                                />
                            </div>
                        </Col>
                    </Row>
                )
            }
            <Row className={'data-container  data-container-primary'}>
                <Col>
                    <table style={{ width: '100%', marginTop: '25px' }}>
                        <thead>
                            <tr className={'column-headers-unbordered'} key={'43434343'}>
                                <th>{submissionHistoryText?.historyTableCompanyName || ''}</th>
                                <th>{submissionHistoryText?.historyTableAccountingPkg || ''}</th>
                                <th style={{ textAlign: 'center' }}>{submissionHistoryText?.historyTableActions || ''}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                connectionsInUse.map((connection, connectionIndex) => {
                                    return (
                                        <tr className={'normal-rows'} key={connection.id}>
                                            <td>{connection.datasetName}</td>
                                            <td>
                                                <div className={'accounting-image-container'}>
                                                    {resolveAccountingPackage(connection.datasourceNameId, accountingPackages)?.friendlyName}
                                                </div>
                                            </td>
                                            <td>
                                                <div
                                                    className={'active-connection-actions-container'}
                                                >
                                                    {getActions(connection, connectionIndex)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </Col>
            </Row>
        </div>
    );

    return connectionsList || noConnections;
}

